import {pollApi} from "@/container";
import {pollEntryActions} from "@/core/http/resources/PollResource";
import {Poll, PollEntry} from "@/program/interfaces/Program";
import Consumer from "@/core/interfaces/consumers/Consumer";

/**
 * Composable orchestrating poll entry submission and cancellation:
 * the entry action(s) run first, then the answer is recorded.
 */
const usePollSubmission = () => {
    /**
     * Submit a poll entry for a given consumer.
     * @param poll  The poll for which the entry is being submitted.
     * @param entry  The poll entry being submitted.
     * @param consumer  The consumer to submit the poll entry for.
     * @param onActionResponses  Called as soon as the entry actions complete,
     *                           before the answer is recorded — the submit call
     *                           can be slow as it refreshes the program statuses.
     * @return The entry action responses, when the entry carries an action.
     */
    const submitPollEntry = async (
        poll: Poll,
        entry: PollEntry,
        consumer: Consumer,
        onActionResponses?: (responses: object[]) => void
    ): Promise<object[] | undefined> => {
        let actionResponses: object[] | undefined;

        if (pollEntryActions(entry).length) {
            actionResponses = await pollApi().actions(entry, consumer);
            onActionResponses?.(actionResponses);
        }

        await pollApi().submit(poll.id, entry.id, consumer.key);

        return actionResponses;
    };

    /**
     * Cancel a previously submitted poll entry for a given consumer.
     * @param poll  The poll for which the entry is being cancelled.
     * @param entry  The poll entry that was submitted.
     * @param consumer  The consumer to cancel the poll entry for.
     * @param actionResponses  The responses of the entry action calls, used by the cancel routes.
     */
    const cancelPollEntry = async (poll: Poll, entry: PollEntry, consumer: Consumer, actionResponses?: object[]): Promise<void> => {
        if (pollEntryActions(entry).some(action => action.cancel_route)) {
            await pollApi().cancelActions(entry, consumer, actionResponses);
        }

        await pollApi().cancel(poll.id, consumer.key);
    };

    return {submitPollEntry, cancelPollEntry};
};

export default usePollSubmission;
